// The GenerativeEngine: wires macro (mood) -> meso (LFO + tension) -> compose/smooth
// per axis -> pitch drift -> Poisson scheduling -> map onsets back to real audio time.
// PURE: no Web Audio / DOM / Pixi. All randomness via injected/seeded mulberry32.

import type { DiatomDescriptor } from '@shared/diatom';
import type { StrikeIntent } from '@shared/intents';
import type { AxisName, ParameterState } from '@shared/parameterState';
import { AXES, createParameterState } from '@shared/parameterState';
import { DIATOMS } from '../diatoms/manifest';
import { FIXED_SEED, mulberry32 } from '@shared/rng';
import type { Rng } from '@shared/rng';

import { OUSmoother, clamp01 } from './smoothing';
import { TimeKeeper } from './timeKeeper';
import { MoodMachine } from './moodMachine';
import { sampleLfos, TensionEnvelope, tensionContribution } from './lfoBank';
import { PitchField } from './pitchField';
import { fillUnits } from './units';
import {
  defaultDials,
  dialBias,
  padBlendTarget,
  padDominantMood,
  type GardeningDials,
} from './dials';
import { Scheduler, type IntentDraft } from './scheduler';

const AXIS_TAU: Record<AxisName, number> = {
  density: 45,
  register: 30,
  brightness: 15,
  harmonicity: 30,
  space: 30,
};

const AXIS_SIGMA: Record<AxisName, number> = {
  density: 0.01,
  register: 0.006,
  brightness: 0.012,
  harmonicity: 0.006,
  space: 0.004,
};

export interface GenerativeEngineOpts {
  rng?: Rng;
  state?: ParameterState;
  seed?: number;
  diatoms?: readonly DiatomDescriptor[];
  scheduleAheadSec?: number;
  timeScale?: 1 | 10;
  enableOuNoise?: boolean;
}

export class GenerativeEngine {
  private readonly _state: ParameterState;
  private readonly rng: Rng;
  private readonly clock: TimeKeeper;
  private readonly mood: MoodMachine;
  private readonly tensionEnv: TensionEnvelope;
  private readonly pitch: PitchField;
  private readonly scheduler: Scheduler;
  private readonly smoothers: Record<AxisName, OUSmoother>;
  private dials: GardeningDials = defaultDials();
  private readonly scheduleAheadSec: number;
  private readonly ouRng: Rng | undefined;

  constructor(opts: GenerativeEngineOpts = {}) {
    this.rng = opts.rng ?? mulberry32(opts.seed ?? FIXED_SEED);
    this._state = opts.state ?? createParameterState();
    this.scheduleAheadSec = opts.scheduleAheadSec ?? 0.6;
    this.clock = new TimeKeeper(opts.timeScale ?? 1);
    this.mood = new MoodMachine(this.rng, this._state.mood.to);
    this.tensionEnv = new TensionEnvelope(this.rng);
    this.pitch = new PitchField(this.rng, this._state.pitchField.rootHz);
    this.scheduler = new Scheduler(opts.diatoms ?? DIATOMS);
    this.ouRng = opts.enableOuNoise ? this.rng : undefined;

    this.smoothers = {} as Record<AxisName, OUSmoother>;
    for (const ax of AXES) {
      this.smoothers[ax] = new OUSmoother(
        this._state.norm[ax],
        AXIS_TAU[ax],
        opts.enableOuNoise ? AXIS_SIGMA[ax] : 0,
      );
    }
  }

  get state(): ParameterState {
    return this._state;
  }

  setTimeScale(scale: number): void {
    this.clock.setTimeScale(scale === 10 ? 10 : 1);
  }

  applyDials(
    dials: Partial<{ calmActive: number; darkBright: number; pureRough: number; roaming: number }>,
  ): void {
    if (dials.calmActive !== undefined) this.dials.calmActive = dials.calmActive;
    if (dials.darkBright !== undefined) this.dials.darkBright = dials.darkBright;
    if (dials.pureRough !== undefined) this.dials.pureRough = dials.pureRough;
    if (dials.roaming !== undefined) this.dials.roaming = dials.roaming;
    this.mood.roaming = this.dials.roaming;
  }

  setMoodPad(point: { x: number; y: number } | null): void {
    this.dials.pad = point;
  }

  forceMood(mood: ParameterState['mood']['to']): void {
    this.mood.forceMood(this.rng, mood);
  }

  /** Set the active pitch scale (preset-driven). */
  setScale(ratios: readonly number[] | undefined): void {
    this.pitch.setScale(ratios);
  }

  /**
   * Snap the smoothed axes straight to the current mood + dial targets. Called on a preset
   * switch so the new scene's character (density, register, brightness…) is felt within a
   * tick instead of gliding in over the 30–60 s smoothing time constants.
   */
  snapToTargets(): void {
    const moodTarget = this.mood.currentMoodTarget();
    for (const ax of AXES) {
      const raw = clamp01(moodTarget[ax] + dialBias(ax, this.dials));
      this.smoothers[ax].value = raw;
      this._state.norm[ax] = raw;
    }
    fillUnits(this._state);
  }

  /** Advance the engine; mutates the shared ParameterState in place. */
  update(realNowSec: number, _dtRealSec?: number): void {
    const dtEngine = this.clock.advance(realNowSec);
    this._state.engineTimeSec = this.clock.engineTimeSec;
    if (dtEngine <= 0) return;

    // 1. macro
    this.mood.step(dtEngine, this.rng);
    const pad = this.dials.pad;
    const moodTarget = pad !== null ? padBlendTarget(pad) : this.mood.currentMoodTarget();
    if (pad !== null) {
      // pad overrides axis targets, but report a pad-flavored mood state
      const dom = padDominantMood(pad);
      this._state.mood = { from: dom, to: dom, blend: 0 };
    } else {
      this._state.mood = this.mood.moodState();
    }

    // 2. meso
    const lfoOff = sampleLfos(this.clock.engineTimeSec, 0.5 + this.dials.roaming);
    const tension = this.tensionEnv.sample(this.clock.engineTimeSec, this.rng);
    this._state.tension = tension;
    const tContrib = tensionContribution(tension);

    // 3. compose + smooth per axis
    for (const ax of AXES) {
      let raw = moodTarget[ax] + lfoOff[ax] + tContrib[ax] + dialBias(ax, this.dials);
      raw = clamp01(raw);
      this._state.norm[ax] = this.smoothers[ax].step(raw, dtEngine, this.ouRng);
    }
    fillUnits(this._state);

    // 4. pitch field drift
    this.pitch.step(
      this.clock.engineTimeSec,
      dtEngine,
      this._state.units.registerCentroidHz,
      this._state.norm.harmonicity,
      this.rng,
    );
    this._state.pitchField = this.pitch.snapshot();
  }

  /** Emit due StrikeIntents up to now+horizon (real audio time). whenSec >= realNowSec always. */
  collect(realNowSec: number, horizonSec: number = this.scheduleAheadSec): StrikeIntent[] {
    const pad = this.dials.pad;
    const mood = pad !== null ? padDominantMood(pad) : this.mood.articulationMood;
    const aheadEngine = horizonSec * this.clock.timeScale;
    const drafts: IntentDraft[] = this.scheduler.advance(
      this.clock.engineTimeSec,
      aheadEngine,
      {
        mood,
        registerNorm: this._state.norm.register,
        registerHz: this._state.units.registerCentroidHz,
        densityOnsetsPerMin: this._state.units.densityOnsetsPerMin,
        densityNorm: this._state.norm.density,
        tension: this._state.tension,
        pureRough: this.dials.pureRough,
        pitchField: this.pitch,
      },
      this.rng,
    );

    const out: StrikeIntent[] = [];
    for (const d of drafts) {
      let whenSec = this.clock.engineToReal(d._onsetEngineSec, realNowSec);
      if (whenSec < realNowSec) whenSec = realNowSec; // safety: never schedule in the past
      const { _onsetEngineSec, ...intent } = d;
      void _onsetEngineSec;
      out.push({ ...intent, whenSec });
    }
    return out;
  }
}

/** Factory mirroring the spec's createMoodEngine naming. */
export function createGenerativeEngine(opts: GenerativeEngineOpts = {}): GenerativeEngine {
  return new GenerativeEngine(opts);
}
